import { usePerms } from 'contexts/PermsContext';
import { AuthContext } from 'features/Auth/contexts/AuthContext';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Button, Collapse, Container } from 'react-bootstrap';
import { IconContext } from 'react-icons';
import { FaChevronDown, FaChevronUp, FaHouse } from 'react-icons/fa6';
import { PiVideoFill, PiWrenchFill } from 'react-icons/pi';
import { RxHamburgerMenu } from 'react-icons/rx';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getLogo } from 'utils/images/image.utils';
import styles from './assets/classicNav.module.css';
import {
  buildSidebarData,
  findOpenDropdownName,
  getEffectiveSelectedPath,
  isDropdownSelected,
} from '../navStateUtils';
/**
 * @param {function} onOpenChanged a funciton listening for when the sidebar open state changes.
 * @param {string} selected the name of the screen that is currently selected. This is so we know which button to highlight as 'selected'
 * @returns
 */
/**
 * The classic mobile navigation — the menu as it was before the 2026-09 navigation redesign
 * (75caa0ee3). Shown when the person picks Classic navigation in the display settings.
 */
export default function ClassicMobileNav({ isOpen, open }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [openDropdownName, setOpenDropdownName] = useState(null);
  const authContext = useContext(AuthContext);
  const { isAuthenticated } = authContext;

  const perms = usePerms();

  const selectedPage = getEffectiveSelectedPath(location);
  const pageButtons = useMemo(
    () => (isAuthenticated ? buildSidebarData(perms) : []),
    [isAuthenticated, perms],
  );
  const publicInfoIndex = pageButtons.findIndex((page) => page.name === 'Public Info');
  const primaryPageButtons =
    publicInfoIndex === -1 ? pageButtons : pageButtons.slice(0, publicInfoIndex + 1);
  const secondaryPageButtons = publicInfoIndex === -1 ? [] : pageButtons.slice(publicInfoIndex + 1);

  useEffect(() => {
    if (!isAuthenticated) {
      setOpenDropdownName(null);
      return;
    }

    setOpenDropdownName(findOpenDropdownName(pageButtons, selectedPage));
  }, [isAuthenticated, pageButtons, selectedPage]);

  useEffect(() => {
    if (!isAuthenticated) perms.reset();
  }, [isAuthenticated]);

  const closeAllDropdowns = () => {
    setOpenDropdownName(null);
  };

  const toggleDropdown = (dropdownName) => {
    setOpenDropdownName((currentDropdownName) =>
      currentDropdownName === dropdownName ? null : dropdownName,
    );
  };

  const scrollMainToTop = () => {
    const mainScroll = document.getElementById('main-scroll-container');
    if (mainScroll?.scrollTo) {
      mainScroll.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const navigateOrScroll = (href) => {
    if (window.location.pathname === href) {
      scrollMainToTop();
      isOpen(false);
      return;
    }
    navigate(href);
    isOpen(false);
  };

  ////////////
  // Styles //
  ////////////

  const ButtonStyleClosed = {
    color: 'var(--color-font)',
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    height: 63,
    alignItems: 'center',
  };

  const ButtonStyleOpen = {
    backgroundColor: 'var(--color-primary)',
    color: 'var(--color-primary-foreground)',
    '--nav-icon-filter': 'var(--high-contrast-nav-icon-filter, none)',
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    height: 63,
    alignItems: 'center',
  };

  const DropButtonStyleOpen = {
    backgroundColor: 'var(--color-primary)',
    color: 'var(--color-primary-foreground)',
    '--nav-icon-filter': 'var(--high-contrast-nav-icon-filter, none)',
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    paddingLeft: 50,
    height: 63,
    alignItems: 'center',
  };

  const DropButtonStyleSelected = {
    backgroundColor: 'var(--color-primary-highlight)',
    color: 'var(--color-primary-highlight-foreground)',
    '--nav-icon-filter':
      'var(--selected-nav-icon-filter, var(--high-contrast-nav-icon-filter, none))',
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    paddingLeft: 50,
    height: 63,
    alignItems: 'center',
  };

  const renderDropdown = (dropdown, index) => {
    if (!dropdown.enabled) return null;

    const dropdownSelected = isDropdownSelected(dropdown, selectedPage);
    const dropdownOpen = openDropdownName === dropdown.name;

    return (
      <div key={index}>
        <button
          type="button"
          className={`btn rounded-0 border-0 px-0 ${styles.sectionButton} ${
            dropdownSelected ? 'fusion-sidebar-selected-item' : ''
          }`}
          style={
            dropdownSelected
              ? { ...DropButtonStyleSelected, paddingLeft: 0 }
              : dropdownOpen
                ? ButtonStyleOpen
                : ButtonStyleClosed
          }
          onClick={() => toggleDropdown(dropdown.name)}
          aria-expanded={dropdownOpen}
        >
          <div className="d-flex justify-content-between w-100">
            <div className="d-flex align-items-center min-w-0">
              <div className={styles['btn-icon']}>{dropdown.icon()}</div>
              <div className={styles.menuLabel}>{dropdown.name}</div>
            </div>
            <div className="me-3">{dropdownOpen ? <FaChevronUp /> : <FaChevronDown />}</div>
          </div>
        </button>
        <Collapse in={dropdownOpen} mountOnEnter unmountOnExit>
          <div className={`overflow-hidden ${styles.submenuList}`}>
            {dropdown.options.map((option, optionIndex) => {
              if (!option.enabled) return null;

              return (
                <Button
                  className="pe-0 py-0 rounded-0 border-0"
                  key={optionIndex}
                  style={
                    selectedPage === option.href ? DropButtonStyleSelected : DropButtonStyleOpen
                  }
                  onClick={() => {
                    navigateOrScroll(option.href);
                  }}
                  variant={selectedPage === option.href ? 'primary' : 'secondary'}
                >
                  <div className="d-flex align-items-center">
                    <div className={styles['btn-icon']}>{option.icon()}</div>
                    <div className={styles.menuLabel}>{option.name}</div>
                  </div>
                </Button>
              );
            })}
          </div>
        </Collapse>
      </div>
    );
  };

  if (isAuthenticated)
    return (
      <div
        className={`fusion-sidebar-shell overflow-x-hidden position-fixed bg-primary-shadow ${
          open && styles.open
        } ${styles.overlay}`}
      >
        <div
          style={{
            marginBottom: 100,
          }}
        >
          {/* Icon header */}
          <Container
            as="header"
            fluid
            className="d-flex justify-content-around p-3 w-100 align-items-center bg-primary"
          >
            <RxHamburgerMenu onClick={() => isOpen(false)} size={30} />
            <Link
              to="/dashboard"
              className="cursor-pointer"
              aria-label="Go to dashboard"
              onClick={() => {
                closeAllDropdowns();
                isOpen(false);
              }}
            >
              <img height={59} src={getLogo(true)} alt="Logo" />
            </Link>
            <div style={{ width: '30px', height: '30px' }}></div>
          </Container>
          {/* Home dashboard */}
          <Button
            className="rounded-0 d-flex w-100 align-items-center px-0"
            style={{ height: 63 }}
            onClick={() => {
              closeAllDropdowns();
              navigateOrScroll('/dashboard');
            }}
            variant={selectedPage === '/dashboard' ? 'primary' : 'dark'}
          >
            <div className="d-flex w-100 align-items-center">
              <div className={styles['btn-icon']}>
                <IconContext.Provider value={{ size: 30 }}>
                  <FaHouse />
                </IconContext.Provider>
              </div>
              <div className={styles.menuLabel}>Dashboard</div>
            </div>
          </Button>
          {primaryPageButtons.map((dropdown, index) => renderDropdown(dropdown, index))}

          {/* ExacqVision VMS — the same permission gate as the desktop Sidebar. */}
          {perms.testPermission(
            [8, 9, 10, 11, 12, 13],
            ['ViewExacqVisionOverwatchVMSConnections'],
          ) && (
            <Button
              className="rounded-0 d-flex w-100 align-items-center px-0"
              style={{ height: 63 }}
              onClick={() => {
                closeAllDropdowns();
                navigateOrScroll('/vms');
              }}
              variant={selectedPage === '/vms' ? 'primary' : 'dark'}
            >
              <div className="d-flex w-100 align-items-center">
                <div className={styles['btn-icon']}>
                  <IconContext.Provider value={{ size: 35 }}>
                    <PiVideoFill />
                  </IconContext.Provider>
                </div>
                <div className={styles.menuLabel}>Overwatch VMS</div>
              </div>
            </Button>
          )}

          {/* Maintenance Dashboard*/}
          {perms.maintenanceLevel > 0 && (
            <Button
              className="rounded-0 d-flex w-100 align-items-center px-0"
              style={{ height: 63 }}
              onClick={() => {
                closeAllDropdowns();
                navigateOrScroll('/maintenancedashboard');
              }}
              variant={selectedPage === '/maintenancedashboard' ? 'primary' : 'dark'}
            >
              <div className="d-flex w-100 align-items-center">
                <div className={styles['btn-icon']}>
                  <IconContext.Provider value={{ size: 35 }}>
                    <PiWrenchFill />
                  </IconContext.Provider>
                </div>
                <div className={styles.menuLabel}>Maintenance Devices</div>
              </div>
            </Button>
          )}

          {secondaryPageButtons.map((dropdown, index) =>
            renderDropdown(dropdown, primaryPageButtons.length + index),
          )}
        </div>
      </div>
    );
}
